import React, { createContext, useEffect, useState } from 'react';

import { useQuery, gql } from '@apollo/client';
import { useLocation } from 'wouter';

import Loader from '../components/Loader';
import WHO_AM_I_QUERY from './WhoAmI.graphql';
import { canRead, canWrite, CRAWFORD_TEAM } from './permissions';

const isCrawOnly = (user) =>
  !user?.isSuperuser &&
  user?.groups?.length === 1 &&
  user.groups[0] === CRAWFORD_TEAM;

const AuthContext = createContext({
  me: null,
  canRead: () => false,
  canWrite: () => false,
  isCrawOnly: () => false,
  shouldHideModule: () => false,
});

const publicPaths = ['/login', '/recover-password', '/reset-password'];

const AuthProvider = ({ children }) => {
  const [location, setLocation] = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const isPublicRoute = publicPaths.some((path) => location?.startsWith(path));

  const { loading, data } = useQuery(gql(WHO_AM_I_QUERY), {
    skip: isPublicRoute,
    fetchPolicy: 'network-only',
    errorPolicy: 'none', // Completely ignore errors to prevent any logging
    notifyOnNetworkStatusChange: false, // Don't notify on network changes
    onCompleted: (data) => {
      // If query completed but no user data, mark for redirect
      if (!isPublicRoute && !data?.me) {
        console.warn(
          'WHO_AM_I query completed but no user data, redirecting to login',
        );
        setShouldRedirect(true);
      }
    },
    onError: () => {
      // If there's any error with the WHO_AM_I query, mark for redirect
      if (!isPublicRoute) {
        console.warn('WHO_AM_I query failed, redirecting to login');
        setShouldRedirect(true);
      }
    },
  });

  // Handle redirect when needed
  useEffect(() => {
    if (shouldRedirect) {
      setLocation('/login');
      setShouldRedirect(false);
    }
  }, [shouldRedirect, setLocation]);

  // Show loader while checking authentication
  if (!isPublicRoute && loading) {
    return <Loader height="100vh" />;
  }

  // Don't render children if user is not authenticated (will redirect)
  if (!isPublicRoute && !data?.me) {
    return <Loader height="100vh" />;
  }

  return (
    <AuthContext.Provider
      value={{
        me: data?.me || null,
        canRead: (module) => canRead(data?.me, module),
        canWrite: (module) => canWrite(data?.me, module),
        isCrawOnly: () => isCrawOnly(data?.me),
        shouldHideModule(module, action = 'read') {
          if (module === 'crawford' && isCrawOnly(data?.me)) {
            return true;
          }
          if (action === 'write' && !canWrite(data?.me, module)) {
            return true;
          }
          return false;
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
